import React, { useCallback, useEffect, useState } from 'react'
import { Provider, useDispatch, useSelector } from 'react-redux'
import createSagaMiddleware from 'redux-saga'
import { applyMiddleware, createStore } from 'redux'
import { composeWithDevTools } from '@redux-devtools/extension'
import { getUser, getTheme, isAuthorized, isFetching } from '../../store/settings/settings.selectors'
import ProfessocialExternalLinks from '@professocial/professocialExternalLinks/src/screens/ProfessocialExternalLinks'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom'
import { registration as registrationCustomerData, privacy, termsOfUse } from '../../shared/translations/customer_json/ergo.json'

// Login
import ProfessocialLogin from '@professocial/professocialLogin/src/screens/ProfessocialLogin'
import { loginRootReducer } from '@professocial/professocialLogin/src/store/loginRoot.reducer'
import { loginRootSagas } from '@professocial/professocialLogin/src/store/loginRoot.sagas'
import { LoginConfiguration, CoreConfiguration, ExternalLinksConfiguration } from '@professocial/professocialTypes/types/configurationTypes'

// Core
import ProfessocialCore from '@professocial/professocialCore/src/screens/ProfessocialCore'
import { coreRootReducer } from '@professocial/professocialCore/src/store/coreRoot.reducer'
import { coreRootSaga } from '@professocial/professocialCore/src/store/coreRoot.sagas'
import { getCurrentUserInitiated, setUser } from '../../store/settings/settings.actions'

import UnauthenticatedRoute from '../../shared/components/UnauthenticatedRoute'
import { ActionCall, Languages } from '@professocial/professocialTypes/types/commonTypes'
import { UserSettings } from '@professocialApi/professocial-core-frontend-api'
import { SettingsRootState } from '../../store/root.reducer'
import { namedRequestData } from '../../store/common/common.selectors'

const sagaMiddleware = createSagaMiddleware()
const storeLogin = createStore(
  loginRootReducer,
  composeWithDevTools(
    applyMiddleware(sagaMiddleware),
  ),
)
sagaMiddleware.run(loginRootSagas)

const storeCore = createStore(
  coreRootReducer,
  composeWithDevTools(
    applyMiddleware(sagaMiddleware),
  ),
)
sagaMiddleware.run(coreRootSaga)

const SelectModule: React.FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const dispatchGetUser = useCallback(() => dispatch(getCurrentUserInitiated()), [dispatch])
  const dispatchSetUser = useCallback((params: UserSettings | undefined) => dispatch(setUser(params)), [dispatch])
  const actionOnGetUser: ActionCall | undefined = useSelector((state: SettingsRootState) => namedRequestData(state, getCurrentUserInitiated.type))

  const user = useSelector(getUser)
  const authorized = useSelector(isAuthorized)
  const fetching = useSelector(isFetching)
  const theme = useSelector(getTheme)
  const hasUser = window.localStorage.getItem('user')
  const intendedUserPage = { from: window.location.pathname }

  const [intendedUserPageAfter, setIntendedUserPageAfter] = useState<{ from: string }>()

  // Definiere öffentliche Routen, die ohne Login erreichbar sein sollen
  const publicRoutes = ['/privacy', '/termsOfUse', '/login', '/resetPassword', '/registerSuccess', '/registerError']
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route))

  const logoutListener: EventListener = () => {
    setTimeout(() => {
      dispatchSetUser(undefined)
      setCoreConfiguration(initialCoreConfiguration)
    }, 500)
  }

  const userListener: EventListener = (({ detail }: CustomEvent) => {
    detail?.id && dispatchGetUser()
  }) as EventListener

  // Login
  const loginConfiguration: LoginConfiguration = {
    theme: theme,
    registrationCustomerData
  }

  // ExternalLinksConfiguration
  const externalLinksConfiguration: ExternalLinksConfiguration = {
    theme: theme,
    data: {
      privacy,
      termsOfUse,
    }
  }

  const initialCoreConfiguration: CoreConfiguration = {
    theme: theme,
    language: [Languages.de, Languages.en],
    logo: '/src/shared/assets/images/logo-customer.png',
    footerLogo: '/src/shared/assets/images/professocial_logo_header.png',
    developmentSystem: true,
    externalLinks: externalLinksConfiguration,
  }

  // Core
  const [coreConfiguration, setCoreConfiguration] = useState<CoreConfiguration>(initialCoreConfiguration)

  useEffect(() => {
    // Nur User laden, wenn es keine öffentliche Route ist
    if (hasUser && !isPublicRoute) {
      dispatchGetUser()
    }

    window.addEventListener('ProfessocialUser', userListener)
    window.addEventListener('ProfessocialLogout', logoutListener)

    return(() => {
      window.removeEventListener('ProfessocialUser', userListener)
      window.removeEventListener('ProfessocialLogout', logoutListener)
    })
  }, [isPublicRoute],
  )

  useEffect(() => {
    user && setCoreConfiguration({ ...coreConfiguration, theme, user, })
  }, [user, theme],
  )

  useEffect(() => {
    const parsedHistory = (history.location.state as { from: string } | undefined)
    if (parsedHistory?.from && !(parsedHistory.from).includes('logout')) {
      setIntendedUserPageAfter(history.location.state as { from: string })
    }
  }, [history.location.state])

  useEffect(() => {
    // Keine Redirects für öffentliche Routen
    if (!isPublicRoute) {
      if (actionOnGetUser?.status === 'success' && intendedUserPageAfter?.from) history.push(intendedUserPageAfter.from)
      if (actionOnGetUser?.status === 'failed' && actionOnGetUser.error.status === 401) history.push('/login', intendedUserPage)
    }
  }, [actionOnGetUser, intendedUserPageAfter, isPublicRoute])

  return (
    <>
      <Switch>
        <Route path='/privacy'>
          <ProfessocialExternalLinks externalLinksConfiguration={externalLinksConfiguration} />
        </Route>
        <Route path='/termsOfUse'>
          <ProfessocialExternalLinks externalLinksConfiguration={externalLinksConfiguration} />
        </Route>
        <UnauthenticatedRoute path={['/login', '/resetPassword/:token', '/registerSuccess', '/registerError']}>
          <Provider store={storeLogin}>
            <ProfessocialLogin loginConfiguration={loginConfiguration} />
          </Provider>
        </UnauthenticatedRoute>

        {user && authorized && theme && !!coreConfiguration?.user && (
          <Provider store={storeCore}>
            <ProfessocialCore coreConfiguration={coreConfiguration} />
          </Provider>
        )}
        {hasUser === null && !user && !authorized && !fetching && !isPublicRoute && <Redirect to={{ pathname: '/login', state: intendedUserPage }} />}
      </Switch>
    </>
  )
}

export default SelectModule
